import React, { useState } from 'react';

import { Link } from 'react-router-dom';

import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  LockOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import {
  IntegratedErps,
  IntegrationEnvironment,
  IntegrationType,
  NgrokIntegrationType,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { TenantIntegrationBodyParams } from '@recurrency/core-api-schema/dist/integrations/postCreateIntegration';
import {
  TenantIntegrationReadTarget,
  TenantIntegrationWriteTarget,
} from '@recurrency/core-api-schema/dist/integrations/tenantIntegrationDTO';
import {
  TenantIntegrationType,
  TenantIntegrationConnectionStatus,
} from '@recurrency/core-api-schema/dist/tenants/tenantUserDTO';
import { Form, Input, message, Space, Steps, Typography, Radio, Collapse, notification } from 'antd';
import Paragraph from 'antd/lib/typography/Paragraph';
import { theme } from 'theme';

import { Button } from 'components/Button';
import { AsyncButton } from 'components/Button/AsyncButton';
import { Container } from 'components/Container';
import { InputFormItem } from 'components/FormItems';
import { Modal } from 'components/Modal';
import { PageHeader } from 'components/PageHeader';
import { RadioGroup } from 'components/Radio';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { truthy } from 'utils/boolean';
import { env } from 'utils/env';
import { captureAndShowError } from 'utils/error';
import { capitalize } from 'utils/formatting';
import { generateTempPassword } from 'utils/password';
import { routes } from 'utils/routes';
import { track, TrackEvent } from 'utils/track';
import { openZendeskModal } from 'utils/zendesk';

import { DownloadNgrokAgentButton } from './DownloadNgrokAgentButton';

const { Step } = Steps;
enum IntegrationStep {
  GettingStarted,
  NetworkConnection,
  M2MTokenSubmission, // NetSuite M2M/SDF credentials (optional)
  DatabaseCredentials,
  ThankYou,
}

enum DatabasePermissionsType {
  ReadAndWrite,
  Read,
}

export interface SelfServeIntegrationFormData {
  name: string;
  type: string;
  host: string;
  port: string;
  username: string;
  password: string;
}

const generatedPassword = generateTempPassword();

export const Integration = () => {
  const { activeTenant, activeUser } = useGlobalApp();

  const [databasePermissionsType, setDatabasePermissionsType] = useState(DatabasePermissionsType.ReadAndWrite);
  const [networkVerificationLoading, setNetworkVerificationLoading] = useState(false);
  const [shouldCollectNetworkFields, setShouldCollectNetworkFields] = useState(false);
  const [p21IntegrationType, setP21IntegrationType] = useState<'cloud' | 'onPrem' | undefined>(undefined);

  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [integrationForm] = Form.useForm();

  const [isM2MModalOpen, setIsM2MModalOpen] = useState(false);
  const [m2mForm] = Form.useForm();

  const [credentialsSubmitted, setCredentialsSubmitted] = useState(false);
  const [connectionTestSuccess, setConnectionTestSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingM2M, setSubmittingM2M] = useState(false);

  // TODO: migrate activeTenant to core-api-schema and TenantDTO
  const [currentStep, setCurrentStep] = useState<IntegrationStep>(0);

  // Helper function to get the next step based on ERP type
  const getNextStep = (currentStepIndex: number) => {
    if (activeTenant.erpType === IntegratedErps.NETSUITE) {
      switch (currentStepIndex) {
        case IntegrationStep.GettingStarted:
          return IntegrationStep.NetworkConnection; // API Token Submission
        case IntegrationStep.NetworkConnection:
          return IntegrationStep.M2MTokenSubmission; // M2M Token Submission
        case IntegrationStep.M2MTokenSubmission:
          return IntegrationStep.ThankYou;
        default:
          return currentStepIndex;
      }
    } else if (activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'cloud') {
      switch (currentStepIndex) {
        case IntegrationStep.GettingStarted:
          return IntegrationStep.DatabaseCredentials;
        case IntegrationStep.DatabaseCredentials:
          return IntegrationStep.ThankYou;
        default:
          return currentStepIndex;
      }
    } else {
      switch (currentStepIndex) {
        case IntegrationStep.GettingStarted:
          return IntegrationStep.NetworkConnection;
        case IntegrationStep.NetworkConnection:
          return IntegrationStep.DatabaseCredentials;
        case IntegrationStep.DatabaseCredentials:
          return IntegrationStep.ThankYou;
        default:
          return currentStepIndex;
      }
    }
  };

  const handleGetStartedNext = async (currentStep: number) => {
    if (activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'onPrem') {
      notification.info({
        message: 'Creating Recurrency Network Agent',
        description: 'Setting up your Recurrency Agent, this may take a minute...',
        duration: 5,
      });
      const { data: agents } = await coreApiFetch(schemas.integrations.getAllAgentsByTenant, {
        pathParams: { tenantId: activeTenant.id },
      });
      if (!agents || agents.items.length === 0) {
        // Create Agent.

        try {
          await coreApiFetch(schemas.integrations.postConnectIntegration, {
            pathParams: { tenantId: activeTenant.id },
            bodyParams: {
              type: NgrokIntegrationType.Database,
            },
          });
        } catch (err) {
          captureAndShowError(
            err,
            'Failed to create Recurrency Agent. Please reach out to our Solutions Engineering team.',
          );
          return;
        }
      }
    }

    setCurrentStep(getNextStep(currentStep));
  };

  const onIntegrationSubmit = async (data: TenantIntegrationBodyParams) => {
    setSubmitting(true);
    try {
      // Always set type and environment for backend validation
      data.type = activeTenant.erpType === IntegratedErps.NETSUITE ? IntegrationType.Api : IntegrationType.Database;
      data.environment = env.APP_ENV as string as IntegrationEnvironment;

      // Auto-generate integration name: "{tenant-slug} {Environment} Integration"
      const environmentName = capitalize(env.APP_ENV as string);
      data.name = `${activeTenant.slug} ${environmentName} Integration`;
      // Map NetSuite fields to expected API fields
      if (activeTenant.erpType === IntegratedErps.NETSUITE) {
        /**
         * NetSuite Credential Mapping Logic
         * ---------------------------------
         *
         * The Recurrency backend expects NetSuite credentials to be mapped to generic database fields for legacy reasons.
         * This section ensures that both legacy and new form field names are supported and mapped correctly for NetSuite integrations.
         *
         * Mapping rules:
         *   - NetSuite Account ID (form: accountId or database) → secrets.database (API), secrets.realm (API), and used to construct secrets.api_host
         *   - NetSuite Token ID (form: tokenId or username)     → secrets.username (API), secrets.api_access_token (API)
         *   - NetSuite Token Secret (form: tokenSecret or password) → secrets.password (API), secrets.api_token_secret (API)
         *   - secrets.api_host is set to `https://{accountId}.suitetalk.api.netsuite.com`
         *
         * This logic ensures compatibility with both legacy forms (which may use accountId, tokenId, tokenSecret)
         * and new forms (which use database, username, password directly).
         *
         * Summary Table:
         * | NetSuite Value | Form Field (legacy/new) | API Payload Field (secrets)                                 |
         * |--------------- |------------------------|-------------------------------------------------------------|
         * | Account ID     | accountId / database   | database, realm, api_host (constructed)                     |
         * | Token ID       | tokenId / username     | username, api_access_token                                  |
         * | Token Secret   | tokenSecret / password | password, api_token_secret                                  |
         *
         * This mapping is required because the backend interprets these generic fields as NetSuite credentials
         * when the integration type is NetSuite, but also supports new NetSuite-specific fields for future compatibility.
         */

        // Extract accountId from either legacy or new field
        const accountId = (data.secrets as any).accountId || data.secrets.database;
        // Map accountId to database and remove legacy field if present
        if ((data.secrets as any).accountId) {
          data.secrets.database = accountId;
          delete (data.secrets as any).accountId;
        }
        // Map accountId to realm and construct api_host URL
        if (accountId) {
          data.secrets.realm = accountId;
          data.secrets.api_host = `https://${accountId}.suitetalk.api.netsuite.com`;
        }
        // Map tokenId to username and remove legacy field if present
        if ((data.secrets as any).tokenId) {
          data.secrets.username = (data.secrets as any).tokenId;
          delete (data.secrets as any).tokenId;
        }
        // Map tokenSecret to password and remove legacy field if present
        if ((data.secrets as any).tokenSecret) {
          data.secrets.password = (data.secrets as any).tokenSecret;
          delete (data.secrets as any).tokenSecret;
        }
        // Also map username/password to NetSuite API-specific fields
        if (data.secrets.username) {
          data.secrets.api_access_token = data.secrets.username;
        }
        if (data.secrets.password) {
          data.secrets.api_token_secret = data.secrets.password;
        }
        // Check if RESTLET integration is enabled (from checkbox)
        const useRestletForRead = (data as any).useRestletApi === true;
        data.syncSources = {
          read: {
            target: useRestletForRead
              ? TenantIntegrationReadTarget.NetsuiteRestletApi
              : TenantIntegrationReadTarget.NetsuiteRestApi,
          },
          write: { target: TenantIntegrationWriteTarget.NetsuiteRestApi },
        };
        // Remove the checkbox field from the data as it's not part of the API schema
        delete (data as any).useRestletApi;
      } else {
        // For P21, handle port conversion and default username
        if (data.secrets.port) {
          data.secrets.port = parseInt(`${data.secrets.port}`, 10);
        }
        data.secrets.username = data.secrets.username || 'recurrencyuser';
        data.syncSources = { read: { target: TenantIntegrationReadTarget.Mssql } };
      }

      const {
        data: { items: integrations },
      } = await coreApiFetch(schemas.integrations.getTenantIntegrations, { pathParams: { tenantId: activeTenant.id } });

      if (integrations.length > 0) {
        await coreApiFetch(schemas.integrations.patchUpdatePartialIntegration, {
          pathParams: { tenantId: activeTenant.id, integrationId: integrations[0].id },
          bodyParams: data,
        });
      } else {
        await coreApiFetch(schemas.integrations.postCreateIntegration, {
          pathParams: { tenantId: activeTenant.id },
          bodyParams: data,
        });
      }
      setIsIntegrationModalOpen(false);
      message.success(
        activeTenant.erpType === IntegratedErps.NETSUITE
          ? `NetSuite credentials submitted successfully.`
          : `Database credentials submitted successfully. You can now test the connection.`,
      );
      // Restore analytics tracking for database credentials submission
      track(TrackEvent.Integrations_DatabaseCredentials_Submit, {
        tenantId: activeTenant.id,
        tenantName: activeTenant.name,
        tenantSchema: activeTenant.databaseSchema,
        userId: activeUser.id,
        userEmail: activeUser.email,
        updateType: integrations.length > 0 ? 'update' : 'create',
      });
      // For P21, stay on the current step to allow connection testing
      if (activeTenant.erpType === IntegratedErps.P21) {
        setCredentialsSubmitted(true);
        // Clear previous test results when resubmitting credentials
        setConnectionTestSuccess(false);
      } else {
        // For NetSuite, advance to next step as before
        setCurrentStep(getNextStep(currentStep));
      }
    } catch (err) {
      captureAndShowError(
        err,
        `Error while submitting ${
          activeTenant.erpType === IntegratedErps.NETSUITE ? 'NetSuite credentials' : 'credentials'
        }. Our team will reach out to resolve this issue.`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onM2MSubmit = async (data: any) => {
    setSubmittingM2M(true);
    try {
      // Get the existing integration (should exist from Step 2)
      const {
        data: { items: integrations },
      } = await coreApiFetch(schemas.integrations.getTenantIntegrations, { pathParams: { tenantId: activeTenant.id } });

      if (integrations.length === 0) {
        throw new Error('No integration found. Please complete Step 2 first.');
      }

      // Prepare the M2M credentials payload (manual entry only for now)
      const m2mPayload: any = {
        secrets: {},
      };

      // Add the M2M credentials to secrets
      if (data.secrets?.account_customization_certificate_id) {
        m2mPayload.secrets.account_customization_certificate_id = data.secrets.account_customization_certificate_id;
      }
      if (data.secrets?.account_customization_private_key) {
        m2mPayload.secrets.account_customization_private_key = data.secrets.account_customization_private_key;
      }
      if (data.secrets?.account_customization_certificate) {
        m2mPayload.secrets.account_customization_certificate = data.secrets.account_customization_certificate;
      }

      // Update the integration with M2M credentials using PATCH
      await coreApiFetch(schemas.integrations.patchUpdatePartialIntegration, {
        pathParams: { tenantId: activeTenant.id, integrationId: integrations[0].id },
        bodyParams: m2mPayload,
      });

      setIsM2MModalOpen(false);
      message.success('M2M credentials saved successfully.');
      setCurrentStep(getNextStep(currentStep));
    } catch (err) {
      captureAndShowError(err, 'Error while submitting M2M credentials.');
    } finally {
      setSubmittingM2M(false);
    }
  };

  const verifyConnection = async () => {
    setNetworkVerificationLoading(true);
    try {
      const res = await coreApiFetch(schemas.integrations.getAgentStatus);
      if (res.data.status) {
        setNetworkVerificationLoading(false);
        setCurrentStep(getNextStep(currentStep));
        message.success('Network connection verified.');
      } else {
        message.error('Agent could not be reached, please try again or reach out to our Solutions Engineering team.');
      }
    } catch (err) {
      captureAndShowError(
        err,
        'Failed to verify network connection, please try again or reach out to our Solutions Engineering team.',
      );
    }
    setNetworkVerificationLoading(false);
  };

  const testDatabaseConnection = async () => {
    const res = await coreApiFetch(schemas.tenants.validateTenantIntegrationConnection, {
      pathParams: { tenantId: activeTenant.id },
      bodyParams: {
        integrationType: TenantIntegrationType.DirectSql,
      },
    });

    if (res.data.status === TenantIntegrationConnectionStatus.Available) {
      setConnectionTestSuccess(true);
      notification.success({
        message: 'Connection Successful',
        description: `Your database credentials are valid and the connection is working properly. Validated at ${new Date(
          res.data.validatedAt,
        ).toLocaleString()}`,
      });
    } else {
      setConnectionTestSuccess(false);
      notification.error({
        message: 'Connection Failed',
        description:
          res.data.error || 'Unable to connect to the database. Please verify your credentials and try again.',
        duration: 10,
      });
    }
  };

  const sqlLines = [
    `create login recurrencyuser with password = '${generatedPassword}'`,
    'create user recurrencyuser for login recurrencyuser with DEFAULT_SCHEMA=[dbo]',
    "exec sp_addrolemember 'db_datareader', 'recurrencyuser'",
    databasePermissionsType === DatabasePermissionsType.ReadAndWrite &&
      "exec sp_addrolemember 'db_datawriter', 'recurrencyuser'",
    databasePermissionsType === DatabasePermissionsType.ReadAndWrite && 'grant execute to recurrencyuser',
  ].filter(truthy);

  const shouldShowPreview = activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === undefined;

  const integrationStepCount =
    activeTenant.erpType === IntegratedErps.NETSUITE
      ? 3 // Getting Started, API Token, M2M Token
      : p21IntegrationType === 'cloud'
      ? 2 // Getting Started, Database Credentials
      : 3; // Getting Started, Network, Database Credentials

  return (
    <Container>
      <PageHeader
        title="Secure Integration Portal"
        headerActions={
          <Space>
            <Link to={routes.settings.users()}>
              <Button icon={<PlusOutlined />}>Invite Technical Contact</Button>
            </Link>
            <Button type="primary" icon={<QuestionCircleOutlined />} onClick={openZendeskModal}>
              Ask an Expert
            </Button>
          </Space>
        }
      />

      {!activeTenant.isActive ? (
        <>
          {shouldShowPreview ? (
            <>
              <Typography.Title level={2}>Welcome to Recurrency! 🎉</Typography.Title>
              <Typography
                className={css`
                  margin-bottom: 20px;
                  margin-top: 0px;
                  font-size: 16px;
                  line-height: 1.6;
                `}
              >
                Before we get started, please select your P21 integration type:
              </Typography>
              <Space>
                <Button type="primary" size="large" onClick={() => setP21IntegrationType('cloud')}>
                  Get Started With P21 Cloud
                  <ArrowRightOutlined />
                </Button>
                <Button type="primary" size="large" onClick={() => setP21IntegrationType('onPrem')}>
                  Get Started With P21 On Prem
                  <ArrowRightOutlined />
                </Button>
              </Space>
            </>
          ) : (
            <>
              <Steps
                current={
                  // Map IntegrationStep enum values to step indices based on ERP type
                  activeTenant.erpType === IntegratedErps.NETSUITE
                    ? currentStep === IntegrationStep.GettingStarted
                      ? 0
                      : currentStep === IntegrationStep.NetworkConnection
                      ? 1
                      : currentStep === IntegrationStep.M2MTokenSubmission
                      ? 2
                      : currentStep === IntegrationStep.ThankYou
                      ? 3
                      : 0
                    : activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'cloud'
                    ? currentStep === IntegrationStep.GettingStarted
                      ? 0
                      : currentStep === IntegrationStep.DatabaseCredentials
                      ? 1
                      : currentStep === IntegrationStep.ThankYou
                      ? 2
                      : 0
                    : currentStep === IntegrationStep.GettingStarted
                    ? 0
                    : currentStep === IntegrationStep.NetworkConnection
                    ? 1
                    : currentStep === IntegrationStep.DatabaseCredentials
                    ? 2
                    : currentStep === IntegrationStep.ThankYou
                    ? 3
                    : 0
                }
                onChange={(c) => {
                  // Map step indices to the correct IntegrationStep enum values based on ERP type
                  if (activeTenant.erpType === IntegratedErps.NETSUITE) {
                    switch (c) {
                      case 0:
                        setCurrentStep(IntegrationStep.GettingStarted);
                        break;
                      case 1:
                        setCurrentStep(IntegrationStep.NetworkConnection);
                        break;
                      case 2:
                        setCurrentStep(IntegrationStep.M2MTokenSubmission);
                        break;
                      case 3:
                        setCurrentStep(IntegrationStep.ThankYou);
                        break;
                      default:
                        setCurrentStep(c);
                    }
                  } else if (activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'cloud') {
                    switch (c) {
                      case 0:
                        setCurrentStep(IntegrationStep.GettingStarted);
                        break;
                      case 1:
                        setCurrentStep(IntegrationStep.DatabaseCredentials);
                        break;
                      case 2:
                        setCurrentStep(IntegrationStep.ThankYou);
                        break;
                      default:
                        setCurrentStep(c);
                    }
                  } else {
                    switch (c) {
                      case 0:
                        setCurrentStep(IntegrationStep.GettingStarted);
                        break;
                      case 1:
                        setCurrentStep(IntegrationStep.NetworkConnection);
                        break;
                      case 2:
                        setCurrentStep(IntegrationStep.DatabaseCredentials);
                        break;
                      case 3:
                        setCurrentStep(IntegrationStep.ThankYou);
                        break;
                      default:
                        setCurrentStep(c);
                    }
                  }
                }}
              >
                <Step title="Getting Started" />
                {activeTenant.erpType === IntegratedErps.NETSUITE && (
                  <>
                    <Step title="API Token Submission" />
                    <Step title="M2M Token Setup" />
                    <Step title="Thank You" />
                  </>
                )}
                {activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'cloud' && (
                  <>
                    <Step title="Database Credentials" />
                    <Step title="Thank You" />
                  </>
                )}
                {activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'onPrem' && (
                  <>
                    <Step title="Network Connection" />
                    <Step title="Database Credentials" />
                    <Step title="Thank You" />
                  </>
                )}
              </Steps>
              <br />
              {currentStep === IntegrationStep.GettingStarted && (
                <>
                  <Typography.Title level={2}>Welcome to Recurrency! 🎉</Typography.Title>
                  <Typography
                    className={css`
                      margin-bottom: 20px;
                      margin-top: 0px;
                      font-size: 16px;
                      line-height: 1.6;
                    `}
                  >
                    We're excited to help you get started with Recurrency and set up your integration with{' '}
                    <strong>{activeTenant.erpType === IntegratedErps.NETSUITE ? 'NetSuite' : 'P21'}</strong>.
                    <br /> <br />
                    This integration will connect Recurrency directly to your{' '}
                    {activeTenant.erpType === IntegratedErps.NETSUITE ? 'NetSuite' : 'P21'} system, allowing you to:
                    <br /> <br />
                    <ul>
                      <li>Access real-time inventory and customer data</li>
                      <li>Create quotes and orders directly in your system</li>
                      <li>Generate accurate forecasts and demand planning</li>
                      <li>Streamline your sales and purchasing workflows</li>
                    </ul>
                    <br />
                    The setup process should take <strong>about 20 minutes</strong> and consists of{' '}
                    {integrationStepCount} main steps:
                    <br /> <br />
                    {activeTenant.erpType === IntegratedErps.NETSUITE && (
                      <ol>
                        <li>
                          <strong>API Token Submission</strong> - Set up NetSuite REST API credentials
                        </li>
                        <li>
                          <strong>M2M Token Setup</strong> - Optional: Configure SuiteCloud Development Framework (SDF)
                          integration
                        </li>
                        <li>
                          <strong>Initial Sync</strong> - Import your existing data into Recurrency
                        </li>
                      </ol>
                    )}
                    {activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'onPrem' && (
                      <ol>
                        <li>
                          <strong>Network Connection</strong> - Establish secure connection to your P21 database
                        </li>
                        <li>
                          <strong>Database Credentials</strong> - Create secure database access for Recurrency
                        </li>
                        <li>
                          <strong>Initial Sync</strong> - Import your existing data into Recurrency
                        </li>
                      </ol>
                    )}
                    {activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'cloud' && (
                      <ol>
                        <li>
                          <strong>Database Credentials</strong> - Create secure database access for Recurrency
                        </li>
                        <li>
                          <strong>Initial Sync</strong> - Import your existing data into Recurrency
                        </li>
                      </ol>
                    )}
                    <br />
                    {activeTenant.erpType === IntegratedErps.NETSUITE && (
                      <>
                        You'll need access to your NetSuite admin panel to complete this setup. If you don't have admin
                        access, you can invite the appropriate person from your team.
                        <br /> <br />
                        <Typography.Title level={5}>What you'll need to complete the setup:</Typography.Title>
                        <Typography>
                          <ul>
                            <li>
                              <strong>Administrator access</strong> to your NetSuite instance
                            </li>
                            <li>
                              <strong>About 20 minutes</strong> to complete the process
                            </li>
                            <li>
                              <strong>Optional:</strong> A NetSuite user license available for a dedicated integration
                              employee
                            </li>
                          </ul>
                        </Typography>
                        <Typography.Title level={5}>Integration steps you'll complete in NetSuite:</Typography.Title>
                        <Typography>
                          <ol>
                            <li>
                              <strong>Enable REST API features</strong> - Enable SuiteTalk REST Web Services and
                              Token-Based Authentication
                            </li>
                            <li>
                              <strong>Install Recurrency Integrator bundle</strong> - Creates the integration and role
                              for Recurrency
                            </li>
                            <li>
                              <strong>Submit your Account ID</strong> - Securely provide your NetSuite Account ID
                            </li>
                            <li>
                              <strong>Create or configure an employee</strong> - Set up a dedicated integration employee
                              or use an existing one
                            </li>
                            <li>
                              <strong>Create access token</strong> - Generate Token ID and Token Secret for secure API
                              access
                            </li>
                          </ol>
                        </Typography>
                        <Typography>
                          <strong>Note:</strong> Our Solutions Engineering team will guide you through each step and
                          help ensure everything is configured correctly.
                        </Typography>
                      </>
                    )}
                    {activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'onPrem' && (
                      <>
                        <Typography
                          className={css`
                            margin-bottom: 20px;
                            margin-top: 0px;
                            font-size: 16px;
                            line-height: 1.6;
                            max-width: 800px;
                          `}
                        >
                          You'll need remote desktop access to the server that runs your P21 database and permissions to
                          create a new database user. If you don't have these permissions, you can invite the
                          appropriate technical contact from your team.
                        </Typography>
                      </>
                    )}
                  </Typography>
                  <Space>
                    <Button
                      type="primary"
                      size="large"
                      permissionsRequired={[TenantUserPermission.ActionEditSettings]}
                      onClick={() => handleGetStartedNext(currentStep)}
                    >
                      Get Started <ArrowRightOutlined />
                    </Button>
                    <Link to={routes.settings.users()}>
                      <Button icon={<PlusOutlined />}>Invite Technical Contact</Button>
                    </Link>
                  </Space>
                </>
              )}
              {currentStep === IntegrationStep.NetworkConnection && (
                <>
                  {activeTenant.erpType === IntegratedErps.NETSUITE ? (
                    <>
                      <Typography.Title level={4}>NetSuite API Token Submission</Typography.Title>
                      <Typography>
                        To connect Recurrency to your NetSuite system, we need to configure the REST API credentials.
                        This allows Recurrency to securely access your NetSuite data and perform operations on your
                        behalf.
                        <br /> <br />
                        You'll need to complete several steps in your NetSuite admin panel and then submit the
                        credentials here.
                      </Typography>
                      <Typography.Title level={5}>📖 Read the Complete Guide First</Typography.Title>
                      <Typography>
                        We strongly recommend reading our{' '}
                        <a
                          href="https://help.recurrency.com/en/articles/11418920-recurrency-and-netsuite-integration-overview"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          detailed NetSuite integration guide
                        </a>{' '}
                        before starting the setup process. The guide includes step-by-step instructions with screenshots
                        and troubleshooting tips.
                        <br /> <br />
                        The steps below are a summary of what you'll need to do in NetSuite.
                      </Typography>
                      <Typography.Title level={5}>Step 1: Enable REST API Features</Typography.Title>
                      <Typography>
                        Navigate to <strong>Setup → Company → Enable Features</strong> in NetSuite:
                        <br /> <br />
                        <ul>
                          <li>
                            Go to the <strong>SuiteCloud</strong> tab
                          </li>
                          <li>
                            Enable <strong>REST WEB SERVICES</strong> under SuiteTalk (Web Services)
                          </li>
                          <li>
                            Enable <strong>TOKEN-BASED AUTHENTICATION</strong> under Manage Authentication
                          </li>
                          <li>
                            Click <strong>Save</strong>
                          </li>
                        </ul>
                      </Typography>
                      <Typography.Title level={5}>Step 2: Install Recurrency Integrator Bundle</Typography.Title>
                      <Typography>
                        Navigate to <strong>Customization → SuiteBundler → Search & Install Bundles</strong>:
                        <br /> <br />
                        <ul>
                          <li>Search for "Recurrency"</li>
                          <li>
                            Select the <strong>Recurrency Integrator</strong> bundle
                          </li>
                          <li>
                            Click <strong>Install Bundle</strong>
                          </li>
                          <li>Wait for the installation to complete (status should show a green checkmark)</li>
                        </ul>
                      </Typography>
                      <Typography.Title level={5}>Step 3: Get Your Account ID</Typography.Title>
                      <Typography>
                        Navigate to <strong>Setup → Integration → SOAP Web Services Preferences</strong>:
                        <br /> <br />
                        <ul>
                          <li>
                            Copy your <strong>Account ID</strong> from the top left corner
                          </li>
                          <li>Submit it in the form below</li>
                        </ul>
                      </Typography>
                      <Typography.Title level={5}>Step 4: Create or Configure an Employee</Typography.Title>
                      <Typography>
                        You have two options for setting up the integration employee:
                        <br /> <br />
                        <strong>Option 1: Create a new employee (recommended if you have available licenses)</strong>
                        <ul>
                          <li>
                            Navigate to <strong>Lists → Employees → Employees → New</strong>
                          </li>
                          <li>Name: "Recurrency Integration"</li>
                          <li>Email: [your-email]@recurrency.com</li>
                          <li>
                            Check <strong>GIVE ACCESS</strong> and <strong>SEND NOTIFICATION EMAIL</strong>
                          </li>
                          <li>
                            Add the <strong>Recurrency Integration Role</strong>
                          </li>
                          <li>
                            Click <strong>Save</strong>
                          </li>
                        </ul>
                        <br />
                        <strong>Option 2: Use an existing employee</strong>
                        <ul>
                          <li>
                            Navigate to <strong>Lists → Employees → Employees</strong>
                          </li>
                          <li>Edit the employee you want to use</li>
                          <li>
                            Add the <strong>Recurrency Integration Role</strong>
                          </li>
                          <li>
                            Click <strong>Save</strong>
                          </li>
                        </ul>
                      </Typography>
                      <Typography.Title level={5}>Step 5: Create Access Token</Typography.Title>
                      <Typography>
                        Navigate to <strong>Setup → Users/Roles → Access Tokens → New</strong>:
                        <br /> <br />
                        <ul>
                          <li>Application Name: "Recurrency Integration"</li>
                          <li>User: Select the employee from Step 4</li>
                          <li>Role: "Recurrency Integration Role"</li>
                          <li>Token Name: "Recurrency Integration Token"</li>
                          <li>
                            Click <strong>Save</strong>
                          </li>
                          <li>
                            Copy the <strong>TOKEN ID</strong> and <strong>TOKEN SECRET</strong> (you'll only see these
                            once!)
                          </li>
                        </ul>
                      </Typography>
                      <Typography.Title level={5}>Submit Your Credentials</Typography.Title>
                      <Typography>
                        Once you've completed all the steps above, submit your credentials securely using the form
                        below:
                      </Typography>
                      <br />
                      <Button icon={<LockOutlined />} type="primary" onClick={() => setIsIntegrationModalOpen(true)}>
                        Submit NetSuite Credentials
                      </Button>
                      <br /> <br />
                      <Typography.Text type="secondary">
                        <strong>Need help?</strong> Our Solutions Engineering team is available to guide you through
                        this process.
                      </Typography.Text>
                    </>
                  ) : (
                    <>
                      <Typography.Title level={4}>Network Connection</Typography.Title>
                      <Typography>
                        Recurrency establishes a secure tunnel to connect to your network without any changes to your
                        firewall.
                        <br /> <br />
                        Let's walk through installing the Recurrency Agent on a machine within your network and
                        verifying that network connection.
                      </Typography>
                      <Typography.Title level={5}>Identifying a host machine</Typography.Title>
                      <Typography>
                        The Recurrency Agent can run on any device connected to your network, but we usually recommend
                        that you run it on the same server as your database.
                      </Typography>
                      <Typography.Title level={5}>Configuring your agent</Typography.Title>
                      <Typography>
                        To configure a custom agent, you will need to know:
                        <br /> <br />
                        <ol>
                          <li>Will you run the agent on the same server as your database or another machine?</li>
                          <li>Does that machine run a 64-bit or 32-bit version of Windows Server?</li>
                          <li>
                            If the agent will run on a separate machine, what is the internal IP of your database
                            server?
                          </li>
                          <li>What port does your database run on?</li>
                        </ol>
                      </Typography>
                      <DownloadNgrokAgentButton />
                      <br /> <br />
                      <Typography.Title level={5}>Installing agent</Typography.Title>
                      <Typography>
                        Once you've downloaded the Recurrency agent, here's what comes next:
                        <br /> <br />
                        <ol>
                          <li>Move the agent zip file to the host machine within your network.</li>
                          <li>Extract the zip in the C: drive and open the agent folder.</li>
                          <li>
                            Open an Administrator Command Prompt and navigate to the agent folder.
                            <br />
                            <Typography.Text italic>
                              Tip: You can quickly navigate to the same folder by copying the path from the header of
                              Windows Explorer and typing "cd [path]"
                            </Typography.Text>
                          </li>
                          <li>Once inside the agent folder, run the "install-agent.bat" command.</li>
                        </ol>
                        That's it! Once the agent is installed, verify connection to move to the next step.
                        <br /> <br />
                      </Typography>
                      <Button
                        icon={<CheckCircleOutlined />}
                        type="primary"
                        onClick={verifyConnection}
                        loading={networkVerificationLoading}
                      >
                        Verify Network Connection
                      </Button>
                      <Button type="text" onClick={() => setCurrentStep(getNextStep(currentStep))}>
                        Continue without verifying <ArrowRightOutlined />
                      </Button>
                      <br /> <br />
                      <Collapse ghost accordion>
                        <Collapse.Panel header="Having trouble with the agent?" key="allowlist">
                          <Typography>
                            If you are unable to install the agent or establish a secure tunnel, Recurrency can also
                            connect to your network if you allowlist the IP address of our private network.
                            <br /> <br />
                            To do this, go to your network settings and allowlist Recurrency's IP address:
                            <br /> <br />
                            <Typography.Text strong copyable>
                              52.8.122.155
                            </Typography.Text>
                          </Typography>
                          <br />
                          <Button
                            type="primary"
                            onClick={() => {
                              setShouldCollectNetworkFields(true);
                              setCurrentStep(getNextStep(currentStep));
                            }}
                          >
                            Next Step <ArrowRightOutlined />
                          </Button>
                        </Collapse.Panel>
                      </Collapse>
                    </>
                  )}
                </>
              )}
              {currentStep === IntegrationStep.M2MTokenSubmission && (
                <>
                  <Typography.Title level={4}>M2M Token Setup (Optional)</Typography.Title>
                  <Typography>
                    Machine-to-Machine (M2M) authentication enables SuiteCloud Development Framework (SDF) integration
                    for NetSuite customizations and deployments. This step is <strong>optional</strong> - skip it if you
                    don't need SDF/RESTLET integration.
                    <br /> <br />
                    M2M authentication uses certificate-based credentials instead of traditional tokens, providing
                    enhanced security for automated deployments and custom integrations.
                  </Typography>

                  <Typography.Title level={5}>Choose Your Approach</Typography.Title>

                  <Typography.Paragraph>
                    <strong>Option 1: Manual Entry</strong>
                    <br />
                    If you already have a certificate generated via OpenSSL, you can manually enter the Certificate ID,
                    Private Key, and Certificate.
                  </Typography.Paragraph>

                  <Typography.Paragraph>
                    <strong>Option 2: Skip This Step</strong>
                    <br />
                    If you don't need SDF integration, you can skip this step and proceed to complete your integration
                    with basic REST API credentials only.
                  </Typography.Paragraph>

                  <br />
                  <Space>
                    <Button icon={<LockOutlined />} type="primary" onClick={() => setIsM2MModalOpen(true)}>
                      Configure M2M Credentials
                    </Button>
                    <Button type="text" onClick={() => setCurrentStep(getNextStep(currentStep))}>
                      Skip this step <ArrowRightOutlined />
                    </Button>
                  </Space>
                </>
              )}
              {currentStep === IntegrationStep.DatabaseCredentials && (
                <>
                  {activeTenant.erpType === IntegratedErps.NETSUITE && <></>}
                  {activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'onPrem' && (
                    <>
                      <Typography.Title level={4}>Database Connection</Typography.Title>
                      <Typography>
                        Recurrency connects directly to your production database in order to ensure data is live and
                        up-to-date at all times.
                        <br /> <br />
                        In this step we'll create a new Recurrency database user to establish this connection. This also
                        makes it easy to trace all database activity performed by Recurrency.
                      </Typography>
                      <Typography.Title level={5}>Generate database credentials</Typography.Title>
                      <Typography>
                        Based on the features your team will use in Recurrency, you can choose between two levels of
                        database access.
                      </Typography>
                      <br />
                      <RadioGroup
                        value={databasePermissionsType}
                        onChange={({ target: { value } }) => setDatabasePermissionsType(value)}
                      >
                        <Space direction="vertical">
                          <Radio value={DatabasePermissionsType.ReadAndWrite}>
                            <strong>Read and Write</strong>: Enables all Recurrency functionality, including creating
                            quotes, orders, prospects, contacts, setting mins, maxes, and more.
                          </Radio>
                          <Radio value={DatabasePermissionsType.Read}>
                            <strong>Read Only</strong>: Enables global search, record lookups, and intelligence
                            including quote recommendations and forecasting, but no record creation.
                          </Radio>
                        </Space>
                      </RadioGroup>
                      <br />
                      <br />
                      <Typography>Based on the permissions you've selected, here's the SQL to run:</Typography>
                      <br />
                      <Paragraph copyable={{ text: sqlLines.join('\n') }}>Copy SQL</Paragraph>
                      <pre
                        className={css`
                          padding: 8px;
                          background: ${theme.colors.neutral[100]};
                          border: 2px solid ${theme.colors.neutral[300]};
                          border-radius: 8px;
                          display: inline-block;
                        `}
                      >
                        <code>
                          {sqlLines.map((sqlLine) => (
                            <>
                              {sqlLine}
                              <br />
                            </>
                          ))}
                        </code>
                      </pre>
                      <br />
                      <Typography>
                        Once you've created Recurrency database credentials, submit them through this secure form:
                      </Typography>
                      <br />
                      <Space>
                        <Button icon={<LockOutlined />} type="primary" onClick={() => setIsIntegrationModalOpen(true)}>
                          Fill & Submit Credentials
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => setCurrentStep(getNextStep(currentStep))}
                          disabled={!credentialsSubmitted}
                        >
                          Next <ArrowRightOutlined />
                        </Button>
                      </Space>
                    </>
                  )}
                  {activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'cloud' && (
                    <>
                      <Typography.Title level={4}>Database Connection</Typography.Title>
                      <Typography>
                        Recurrency connects directly to your Epicor provided read-replica database in order to ensure
                        data is live and up-to-date at all times.
                        <br /> <br />
                        In this step we'll need to collect the credentials from Epicare.
                      </Typography>
                      <Typography.Title level={5}>Find read-replica database credentials</Typography.Title>
                      <Typography>
                        Epicor provides a read-replica database and its details can be found inside your P21 instance.
                        To find the right credentials, please follow the steps below:
                        <div
                          className={css`
                            display: block;
                            margin-left: 16px;
                            max-width: 600px;
                          `}
                        >
                          <ol>
                            <li>
                              <Link to="https://epiccare.epicor.com/epiccare">Log into the EpicCare Portal.</Link>
                            </li>
                            <li>
                              Go to the Main Home Screen, navigate to the top right, hover over your name, and select
                              "Account Administration".
                            </li>
                            <li>
                              Choose the Production Configuration Profile on your left (this should be your production
                              Prophet 21 PCP) then select the account name associated with your live production
                              environment.
                            </li>
                            <li>
                              <span>Look for the following fields:</span>
                              <ul>
                                <li>
                                  <strong>Replicated Database Name</strong>(e.g. az_yourname_live)
                                </li>
                                <li>
                                  <strong>Replication DB User</strong> (e.g. readonly_yourname_live)
                                </li>
                                <li>
                                  <strong>Replicated DB Password</strong>
                                </li>
                                <li>
                                  <strong>Replicated SQL server</strong> (e.g. p21us-read03.epicordistribution.com)
                                </li>
                                <li>
                                  <strong>ECC URL</strong> (e.g. 50135)
                                </li>
                              </ul>
                            </li>
                          </ol>
                        </div>
                        When you are ready, please click the Submit Credentials button below so you can submit securely
                        to us through the secure form. Once we have confirmed that your credentials are correct, we will
                        begin the sync process, which usually takes 24-72 hours to complete.
                      </Typography>
                      <br />
                      <Space>
                        <Button icon={<LockOutlined />} type="primary" onClick={() => setIsIntegrationModalOpen(true)}>
                          Fill & Submit Credentials
                        </Button>
                        <AsyncButton
                          icon={<CheckCircleOutlined />}
                          onClick={testDatabaseConnection}
                          disabled={!credentialsSubmitted}
                        >
                          Test Connection
                        </AsyncButton>
                        <Button
                          type="primary"
                          onClick={() => setCurrentStep(getNextStep(currentStep))}
                          disabled={!connectionTestSuccess}
                        >
                          Next <ArrowRightOutlined />
                        </Button>
                      </Space>
                    </>
                  )}
                </>
              )}
              {currentStep === IntegrationStep.ThankYou && (
                <>
                  <Typography.Title level={2}>Thank you for submitting your credentials!</Typography.Title>
                  <Typography>
                    Our Solutions Engineering team will review your credentials and begin the initial sync process. Once
                    everything is complete, we will reach out to let you know that your Recurrency account is ready to
                    use.
                  </Typography>
                </>
              )}
            </>
          )}
        </>
      ) : (
        <Typography>
          <Typography.Title level={5}>ERP integration complete!</Typography.Title>
          <Link to={routes.sales.dashboard()}>
            <Button type="primary">
              Open Dashboard <ArrowRightOutlined />
            </Button>
          </Link>
        </Typography>
      )}
      <Modal
        visible={isIntegrationModalOpen}
        title={activeTenant.erpType === IntegratedErps.NETSUITE ? 'NetSuite Credentials' : 'Database Credentials'}
        onCancel={() => setIsIntegrationModalOpen(false)}
        footer={
          <div>
            <Button onClick={() => setIsIntegrationModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              permissionsRequired={[TenantUserPermission.ActionEditSettings]}
              onClick={integrationForm.submit}
              loading={submitting}
            >
              Submit
            </Button>
          </div>
        }
        centered
        width={450}
      >
        <Form layout="vertical" name="integrationForm" form={integrationForm} onFinish={onIntegrationSubmit}>
          {activeTenant.erpType === IntegratedErps.NETSUITE && (
            <>
              <InputFormItem
                name={['secrets', 'database']}
                label="NetSuite Account ID"
                rules={[{ required: true, message: 'Please add your NetSuite Account ID.' }]}
                placeholder="e.g., 1234567"
              />
              <InputFormItem
                name={['secrets', 'username']}
                label="Token ID"
                rules={[{ required: true, message: 'Please add your Token ID.' }]}
                placeholder="Enter your NetSuite Token ID"
              />
              <Form.Item
                label="Token Secret"
                name={['secrets', 'password']}
                rules={[{ required: true, message: 'Please add your Token Secret.' }]}
              >
                <Input.Password
                  className={css`
                    height: 40px;
                    border-radius: 8px;
                  `}
                  placeholder="Enter your NetSuite Token Secret"
                />
              </Form.Item>
            </>
          )}
          {activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'onPrem' && (
            <>
              {shouldCollectNetworkFields && (
                <>
                  <InputFormItem
                    name={['secrets', 'host']}
                    label="Host Address"
                    rules={[{ required: true, message: 'Please add a host address.' }]}
                  />

                  <InputFormItem
                    name={['secrets', 'port']}
                    label="Port"
                    type="number"
                    rules={[{ required: true, message: 'Please add a port.' }]}
                  />
                </>
              )}

              <InputFormItem
                name={['secrets', 'database']}
                label="Database Name"
                rules={[{ required: true, message: 'Please add a database name.' }]}
              />

              <InputFormItem name={['secrets', 'username']} label="Username" defaultValue="recurrencyuser" />

              <Form.Item
                label="Password"
                name={['secrets', 'password']}
                rules={[{ required: true, message: 'Please add a password.' }]}
                initialValue={generatedPassword}
              >
                <Input.Password
                  className={css`
                    height: 40px;
                    border-radius: 8px;
                  `}
                />
              </Form.Item>
            </>
          )}
          {activeTenant.erpType === IntegratedErps.P21 && p21IntegrationType === 'cloud' && (
            <>
              <InputFormItem
                name={['secrets', 'database']}
                label="Replicated Database Name"
                rules={[{ required: true, message: 'Please add a database name.' }]}
              />

              <InputFormItem
                name={['secrets', 'username']}
                label="Replicated Database Username"
                defaultValue="recurrencyuser"
              />

              <Form.Item
                label="Replicated Database Password"
                name={['secrets', 'password']}
                rules={[{ required: true, message: 'Please add a password.' }]}
                initialValue={generatedPassword}
              >
                <Input.Password
                  className={css`
                    height: 40px;
                    border-radius: 8px;
                  `}
                />
              </Form.Item>

              <InputFormItem
                name={['secrets', 'host']}
                label="Replicated SQL Server"
                rules={[{ required: true, message: 'Please add a replicated SQL server.' }]}
              />

              <InputFormItem
                name={['secrets', 'port']}
                label="ECC URL"
                rules={[{ required: true, message: 'Please add an ECC URL (numeric).' }]}
              />
            </>
          )}
        </Form>
      </Modal>

      {/* M2M Token Modal */}
      <Modal
        visible={isM2MModalOpen}
        title="NetSuite M2M (SDF) Credentials"
        onCancel={() => setIsM2MModalOpen(false)}
        footer={
          <div>
            <Button onClick={() => setIsM2MModalOpen(false)} disabled={submittingM2M}>
              Cancel
            </Button>
            <Button
              type="primary"
              permissionsRequired={[TenantUserPermission.ActionEditSettings]}
              onClick={m2mForm.submit}
              loading={submittingM2M}
            >
              Submit
            </Button>
          </div>
        }
        centered
        width={600}
      >
        <Form layout="vertical" name="m2mForm" form={m2mForm} onFinish={onM2MSubmit}>
          <Typography.Text style={{ display: 'block', marginBottom: 16 }}>
            Configure Machine-to-Machine authentication for SuiteCloud Development Framework (SDF) integration.
          </Typography.Text>

          {/* TODO: Auto-generate feature temporarily disabled until button-based flow is implemented
              Will be redesigned to: Generate & Download button → User uploads to NetSuite → User pastes Cert ID in same modal */}

          {/* MANUAL ENTRY WORKFLOW (Use Case 2) */}
          <>
            <Typography.Paragraph>
              <strong>Step 1: Generate Certificate with OpenSSL</strong>
            </Typography.Paragraph>
            <Paragraph
              copyable={{
                text: `openssl req -x509 -newkey rsa:4096 -sha256 -keyout private_key.pem -out certificate.pem -days 730 -nodes -subj "/CN=${activeTenant.slug} Automation/O=Recurrency/C=US"`,
              }}
            >
              Copy OpenSSL Command
            </Paragraph>
            <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, fontSize: 11 }}>
              {`openssl req -x509 -newkey rsa:4096 -sha256 -keyout private_key.pem \\
  -out certificate.pem -days 730 -nodes \\
  -subj "/CN=${activeTenant.slug} Automation/O=Recurrency/C=US"`}
            </pre>

            <Typography.Paragraph style={{ marginTop: 16 }}>
              <strong>Step 2: Upload to NetSuite</strong>
              <br />
              Navigate to: <strong>Setup → Integration → OAuth 2.0 M2M Setup</strong>
              <br />
              Upload your certificate.pem file to NetSuite and copy the generated Certificate ID.
            </Typography.Paragraph>

            <Typography.Paragraph>
              <strong>Step 3: Paste credentials below</strong>
              <br />
              Copy and paste the Certificate ID, private key, and certificate contents into the fields below.
            </Typography.Paragraph>

            <InputFormItem
              name={['secrets', 'account_customization_certificate_id']}
              label="Certificate ID"
              placeholder="e.g., custcert_123456789"
              help="The Certificate ID from NetSuite (optional - can be added later)"
            />

            <Form.Item
              label="Private Key (PEM format)"
              name={['secrets', 'account_customization_private_key']}
              help="Paste the entire contents of your private_key.pem file"
            >
              <Input.TextArea
                placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                className={css`
                  font-family: monospace;
                  font-size: 11px;
                  -webkit-text-security: disc;
                `}
                rows={4}
              />
            </Form.Item>

            <Form.Item
              label="Certificate (PEM format)"
              name={['secrets', 'account_customization_certificate']}
              help="Paste the entire contents of your certificate.pem file"
            >
              <Input.TextArea
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                className={css`
                  font-family: monospace;
                  font-size: 11px;
                  -webkit-text-security: disc;
                `}
                rows={4}
              />
            </Form.Item>
          </>
        </Form>
      </Modal>
    </Container>
  );
};
